import type { TicketType } from '../../../modules/support/types/support';

/**
 * Tabs exposed by the SupportWidget modal that accept a pre-filled
 * title/description (the form-backed ticket tabs). The FAQ/NPS tabs are
 * not pre-fillable.
 */
export type SupportWidgetTab = 'bug' | 'feature';

/**
 * Payload carried on the `janus:open-support` CustomEvent so any surface
 * can open the in-app support widget already pre-filled — without coupling
 * to the widget's internal React state.
 */
export interface OpenSupportWidgetDetail {
  /** Which form tab to land on. Defaults to `feature` (a request, not a bug). */
  tab?: SupportWidgetTab;
  /** Ticket type sent to the backend. Defaults to `billing` for downgrades. */
  type?: TicketType;
  /** Pre-filled ticket title (maps to the form `title` field). */
  title?: string;
  /** Pre-filled ticket body (maps to the form `description` field). */
  description?: string;
}

/** Event name the floating SupportWidget listens for (see SupportWidget.tsx). */
export const OPEN_SUPPORT_EVENT = 'janus:open-support';

/**
 * Open the existing in-app support widget, optionally pre-filling the
 * ticket form. Used by the billing downgrade flows (voice tier + base
 * plan cards) to route a downgrade to a SUPPORT TICKET instead of
 * executing the change (owner decision).
 *
 * Implemented as a window CustomEvent so it works from anywhere in the
 * tree (the widget lives once in AppLayout) and gracefully no-ops in a
 * non-DOM context (SSR/tests).
 */
export function openSupportWidget(detail: OpenSupportWidgetDetail = {}): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent<OpenSupportWidgetDetail>(OPEN_SUPPORT_EVENT, { detail }));
}
